import React, { useCallback, useEffect, useState } from 'react';
import { Col, Form, Input, Row } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StyledForm, StyledPreview } from './StyledForm';
import { ButtonActionPage } from '../../../atoms';
import { InsightsPreview } from '../../../molecules/preview/tiles';
import { HomeWidgetsRecord } from '../../../../repository/mytm/home/homeWidgetsRepository';
import { HomeGroupKey, HomeType, HomeWidgetStatus, HomeWidgetsForm } from '../../../../types/home';
import { HOME } from '../../../../constants/define/translation';
import { DEFAULT_HOME } from '../../../../constants/home';
import HomeTileRecordId from '../HomeTileRecordId';

const DEFAULT_INSIGHTS = {
  name: undefined,
  heading: DEFAULT_HOME.INSIGHTS.HEADING,
  title: DEFAULT_HOME.INSIGHTS.TITLE,
  description: DEFAULT_HOME.INSIGHTS.DESCRIPTION,
  ctaText: undefined,
  ctaLink: undefined,
};

interface FormInsightsProps {
  onSaveHomeWidgets: (values: any) => void;
  record?: HomeWidgetsRecord;
}

export const FormInsights = (props: FormInsightsProps) => {
  const { t } = useTranslation(HOME);
  const { onSaveHomeWidgets, record } = props;
  const [form] = Form.useForm();

  const [dataForm, setDataForm] = useState<HomeWidgetsForm>(DEFAULT_INSIGHTS);

  const onFinish = useCallback(
    (values: HomeWidgetsForm) => {
      onSaveHomeWidgets({
        ...values,
        status: record?.status || HomeWidgetStatus.INACTIVE,
        groupKey: HomeGroupKey.HOME_TILES,
        type: HomeType.INSIGHT_OVERVIEW,
        extraData: DEFAULT_HOME.INSIGHTS.EXTRA_DATA,
        backgroundColor: record?.backgroundColor || DEFAULT_HOME.INSIGHTS.COLOR,
        backgroundImage: record?.backgroundImage,
        contentType: record?.contentType,
        contentId: record?.contentId,
        productId: record?.productId,
        contentFormat: record?.contentFormat,
        show: record?.show,
      });
    },
    [record, onSaveHomeWidgets],
  );

  const onChangeInput = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  useEffect(() => {
    if (record) {
      const formValue = {
        name: record.name,
        heading: record.heading,
        title: record.title,
        description: record.description,
        ctaText: record.ctaText,
        ctaLink: record.ctaLink,
      };
      form.setFieldsValue(formValue);
      setDataForm(formValue);
    } else {
      form.setFieldsValue(DEFAULT_INSIGHTS);
    }
  }, [record]);

  return (
    <>
      <Form layout="vertical" form={form} onFinish={onFinish} name="from-insights">
        <Row>
          <StyledPreview span={9}>
            <InsightsPreview values={dataForm} />
          </StyledPreview>
          <StyledForm span={15}>
            <div className="form-scroll">
              {record?.id && <HomeTileRecordId id={record?.id} />}
              <Form.Item
                name="name"
                label={t('form.cardName')}
                rules={[{ required: true, message: t('validate.nameBlank') }]}
              >
                <Input placeholder={t('form.cardName')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <Form.Item
                name="heading"
                label={t('form.labelOfCard')}
                rules={[{ required: true, message: t('validate.labelBlank') }]}
              >
                <Input placeholder={t('form.labelOfCardPlaceholder')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <Form.Item
                name="title"
                label={t('form.description')}
                rules={[{ required: true, message: t('validate.titleBlank') }]}
              >
                <Input placeholder={t('form.descriptionTitle')} maxLength={255} onChange={onChangeInput} />
              </Form.Item>
              <Form.Item name="description" rules={[{ required: true, message: t('validate.descriptionBlank') }]}>
                <Input placeholder={t('form.descriptionPlaceholder')} onChange={onChangeInput} />
              </Form.Item>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name="ctaText"
                    label={t('form.button')}
                    rules={[{ required: true, message: t('validate.buttonText') }]}
                  >
                    <Input placeholder={t('form.buttonText')} maxLength={50} onChange={onChangeInput} />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2} className="buttonLink">
                  <Form.Item name="ctaLink" label=" " rules={[{ required: true, message: t('validate.buttonLink') }]}>
                    <Input placeholder={t('form.buttonLink')} maxLength={200} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <ButtonActionPage
              text={record ? t('btnUpdateCard') : t('btnCreateCard')}
              id="1"
              className="btn-submit-form-home"
              htmlType="submit"
              icon={!record ? <FileAddOutlined /> : undefined}
            />
          </StyledForm>
        </Row>
      </Form>
    </>
  );
};
